import { t } from 'i18next';
import { useId } from 'react';
import { cn, SHIMMER_CLASSES } from '../../../lib/styles';
import { Button } from '../button/button.spark';
import { Text } from '../text/text.spark';
import { paginationCountsLabel } from './table.shared';
import type { TableCellProps, TablePagination, TableProps, TableRowProps } from './types';
import { DEFAULT_SKELETON_COLS, DEFAULT_SKELETON_ROWS } from './types';

export function Table(props: TableProps) {
  const captionId = useId();
  const { pagination, loading, skeleton } = props;

  const hasHeadings = props.headings.length > 0;
  const colCount = hasHeadings ? props.headings.length : (skeleton?.cols ?? DEFAULT_SKELETON_COLS);
  const rowCount = skeleton?.rows ?? DEFAULT_SKELETON_ROWS;
  const canPaginate = !!pagination && (pagination.hasNext || pagination.hasPrevious);

  return (
    <div className="h-auto w-full overflow-x-auto rounded-lg border border-gray-200 bg-white text-sm text-spark-text-secondary shadow-sm">
      <table
        className={cn('w-full', loading ? 'table-fixed' : 'table-auto')}
        aria-labelledby={captionId}
      >
        <caption id={captionId} className="sr-only">
          {props.caption}
        </caption>
        <thead className="border-gray-200 border-b uppercase tracking-wider">
          <tr>
            {hasHeadings
              ? props.headings.map((h) => (
                  <th
                    scope="col"
                    className="whitespace-nowrap px-4 py-2.5 text-start align-middle font-medium text-gray-700 text-xs"
                    key={h}
                  >
                    {h}
                  </th>
                ))
              : loading &&
                Array.from({ length: colCount }).map((_, i) => (
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-start align-middle"
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
                    key={`skeleton-head-${i}`}
                  >
                    <div className={cn('h-3 w-full rounded', SHIMMER_CLASSES)} />
                  </th>
                ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {loading
            ? Array.from({ length: rowCount }).map((_, rowIdx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
                <SkeletonRow key={`skeleton-row-${rowIdx}`} cols={colCount} rowIndex={rowIdx} />
              ))
            : props.children}
        </tbody>
      </table>
      {!loading && canPaginate && <Pagination {...pagination} />}
    </div>
  );
}

function Pagination(props: TablePagination) {
  return (
    <div
      className={cn(
        'flex items-center border-gray-200 border-t px-4 py-2',
        props.counts ? 'justify-between' : 'justify-end',
      )}
    >
      {props.counts && (
        <Text size="md" variant="secondary">
          {paginationCountsLabel(props.counts)}
        </Text>
      )}
      <div className="flex gap-2">
        {props.hasPrevious && (
          <Button
            variant="secondary"
            aria-label={t('aria:previous_page')}
            onClick={props.onPrevious}
          >
            {t('buttons:previous')}
          </Button>
        )}
        {props.hasNext && (
          <Button variant="secondary" aria-label={t('aria:next_page')} onClick={props.onNext}>
            {t('buttons:next')}
          </Button>
        )}
      </div>
    </div>
  );
}

function SkeletonRow({ cols, rowIndex }: { cols: number; rowIndex: number }) {
  return (
    <tr aria-hidden>
      {Array.from({ length: cols }).map((_, colIdx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
        <td key={`skeleton-${rowIndex}-${colIdx}`} className="px-4 py-3 align-middle">
          <div className={cn('h-4 w-full rounded-full', SHIMMER_CLASSES)} />
        </td>
      ))}
    </tr>
  );
}

function Row(props: TableRowProps) {
  return (
    <tr
      className={cn('whitespace-nowrap transition', {
        'cursor-pointer hover:bg-gray-50': props.onClick,
        'bg-gray-100': props.selected,
      })}
      id={props.id}
      onClick={props.onClick}
    >
      {props.children}
    </tr>
  );
}

function Cell(props: TableCellProps) {
  return (
    <td
      className={cn('px-4 py-2 align-middle', {
        'sticky end-0': props.sticky,
      })}
    >
      <span className={cn('flex gap-2', props.sticky && 'justify-end')}>{props.children}</span>
    </td>
  );
}

Table.Row = Row;
Table.Cell = Cell;
